/**
 * Admin screen for the activation-code system: current code + regenerate,
 * active sessions with their location, and kick / kick-and-block per
 * session. See relay/activation.ts for what each action actually does.
 */

import {
  AdminAuthError,
  fetchCurrentCode,
  fetchMaxScanAttempts,
  fetchSessions,
  fetchVenues,
  getStoredAdminSecret,
  kickAndBlockSession,
  kickSession,
  regenerateActivationCode,
  revokeAllSessionsApi,
  saveMaxScanAttemptsApi,
  storeAdminSecret,
  type SessionInfo,
  type Venue,
} from '../adminApi';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

export interface AccessControlActions {
  back(): void;
}

/**
 * Access Control as venue staff see it (Indra, 2026-10-09): today's activation code and Regenerate, nothing else — no sessions,
 * no kicking, no scan limit. Regenerating changes the code for everyone (a phone is asked for the new one when it starts a game),
 * so it is asked about first.
 */
export function renderActivationCodeOnly(root: HTMLElement, actions: AccessControlActions): void {
  root.innerHTML = `${renderBrandHeader()}<div class="card"><h1>Activation code</h1><p class="muted">Loading…</p></div>`;
  void (async () => {
    try {
      showCode(root, actions, await fetchCurrentCode(), undefined, false);
    } catch (e) {
      if (e instanceof AdminAuthError) {
        showCode(root, actions, undefined, 'That code is no longer valid. Go back and enter it again.', false);
        return;
      }
      showCode(root, actions, undefined, 'Could not load the code. Check your connection.', false);
    }
  })();
}

function showCode(root: HTMLElement, actions: AccessControlActions, code: string | undefined, error: string | undefined, confirming: boolean): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Activation code</h1>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      <label>Today's activation code</label>
      <input type="text" readonly value="${esc(code ?? '—')}" style="font-family:monospace;font-size:1.4rem;width:100%" />
      <p class="muted">Rotates by itself every day at 4 AM.</p>
      ${
        confirming
          ? `<p>Make a new code now? The old one stops working: players are asked for the new one when they start a game.</p>
             <div class="row">
               <button type="button" id="regenYes" class="danger">Yes, make a new code</button>
               <button type="button" id="regenNo" class="ghost">Cancel</button>
             </div>`
          : '<button type="button" id="regenerateBtn" class="secondary" style="width:100%">Regenerate</button>'
      }
    </div>
    <button type="button" id="backBtn" class="ghost" style="width:100%">Back</button>
  `;
  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#regenerateBtn')?.addEventListener('click', () => showCode(root, actions, code, error, true));
  root.querySelector<HTMLButtonElement>('#regenNo')?.addEventListener('click', () => showCode(root, actions, code, undefined, false));
  root.querySelector<HTMLButtonElement>('#regenYes')?.addEventListener('click', () => {
    void (async () => {
      try {
        showCode(root, actions, await regenerateActivationCode(), undefined, false);
      } catch {
        showCode(root, actions, code, 'The code was not changed. Check your connection and try again.', false);
      }
    })();
  });
}

export function renderAccessControl(root: HTMLElement, actions: AccessControlActions): void {
  if (!getStoredAdminSecret()) {
    renderSecretPrompt(root, actions, undefined);
    return;
  }
  void loadAndRender(root, actions);
}

function renderSecretPrompt(root: HTMLElement, actions: AccessControlActions, error: string | undefined): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Admin Secret Required</h1>
      <p class="muted">
        Separate from the branding PIN — this authorizes server-side actions like viewing or kicking a session.
        Enter it once; it's remembered on this device after that.
      </p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="secretInput">Admin secret</label>
      <input type="password" id="secretInput" autocomplete="off" />

      <div class="row" style="margin-top:1rem">
        <button type="button" id="backBtn" class="secondary">Back</button>
        <button type="button" id="saveBtn">Continue</button>
      </div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#saveBtn')!.addEventListener('click', () => {
    const secret = root.querySelector<HTMLInputElement>('#secretInput')!.value.trim();
    if (!secret) {
      renderSecretPrompt(root, actions, 'Enter the admin secret.');
      return;
    }
    storeAdminSecret(secret);
    void loadAndRender(root, actions);
  });
}

async function loadAndRender(root: HTMLElement, actions: AccessControlActions): Promise<void> {
  root.innerHTML = `${renderBrandHeader()}<div class="card"><h1>Access Control</h1><p class="muted">Loading…</p></div>`;
  try {
    const [code, sessions, venues, maxScanAttempts] = await Promise.all([
      fetchCurrentCode(),
      fetchSessions(),
      fetchVenues(),
      fetchMaxScanAttempts(),
    ]);
    renderScreen(root, actions, code, sessions, venues, maxScanAttempts, undefined);
  } catch (e) {
    if (e instanceof AdminAuthError) {
      renderSecretPrompt(root, actions, 'Incorrect admin secret.');
      return;
    }
    renderScreen(root, actions, undefined, [], [], undefined, 'Could not load access control data. Check your connection.');
  }
}

/**
 * `session.venueName` is a snapshot taken by the relay the moment this device last activated or re-verified — it does NOT
 * update just because an admin edits a venue's radius afterward. So the match is recomputed here from what the session
 * keeps (the nearest venue and the distance to it, rounded UP to 10 m) against today's venue list: a radius change shows
 * at once. Display only: it never touches the stored session, and real access decisions still happen only in an actual
 * verify/activate call. The session no longer holds the device's coordinates at all.
 */
export function liveVenueMatch(session: SessionInfo, venues: Venue[]): Venue | undefined {
  if (session.nearestVenue === undefined || typeof session.nearestDistanceM !== 'number') return undefined;
  const venue = venues.find((v) => v.name === session.nearestVenue);
  return venue && session.nearestDistanceM <= venue.radiusMeters ? venue : undefined;
}

function locationLabel(session: SessionInfo, venues: Venue[]): string {
  const match = liveVenueMatch(session, venues);
  if (match) return `At ${match.name}`;
  if (session.city || session.country) {
    return `Outside any registered venue (${[session.city, session.country].filter(Boolean).join(', ')})`;
  }
  return 'Location unknown';
}

/** Only worth showing when a device isn't currently inside a venue — "nearest" to a venue it's already at is trivially that same venue at ~0m. */
export function nearestVenueLabel(session: SessionInfo, venues: Venue[]): string | undefined {
  if (liveVenueMatch(session, venues)) return undefined;
  if (session.nearestVenue === undefined || typeof session.nearestDistanceM !== 'number') return undefined;
  return `Nearest: ${session.nearestVenue} (about ${formatDistance(session.nearestDistanceM)} away)`;
}

function renderScreen(
  root: HTMLElement,
  actions: AccessControlActions,
  code: string | undefined,
  sessions: SessionInfo[],
  venues: Venue[],
  maxScanAttempts: number | undefined,
  error: string | undefined,
): void {
  const sessionRows = sessions
    .map((s) => {
      const nearest = nearestVenueLabel(s, venues);
      return `
      <div class="card" style="margin-top:0.5rem">
        <div><strong>${esc(locationLabel(s, venues))}</strong></div>
        ${nearest ? `<div class="muted">${esc(nearest)}</div>` : ''}
        <div class="muted">Table: ${s.tableId ? esc(s.tableId) : 'no game started yet'}</div>
        <div class="muted">Since ${esc(new Date(s.issuedAt).toLocaleString())}</div>
        ${s.returnedAfterBlock ? '<div class="error-box" style="margin-top:0.5rem">Previously blocked — auto-unblocked after returning to a registered venue.</div>' : ''}
        <div class="row" style="margin-top:0.5rem">
          <button type="button" class="secondary kick-btn" data-session-id="${esc(s.id)}">Kick</button>
          <button type="button" class="danger kick-block-btn" data-session-id="${esc(s.id)}">Kick &amp; Block Device</button>
        </div>
      </div>
    `;
    })
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Access Control</h1>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label>Today's activation code</label>
      <div class="row" style="align-items:center">
        <input type="text" readonly value="${esc(code ?? '—')}" style="font-family:monospace;font-size:1.2rem;flex:1" />
        <button type="button" id="regenerateBtn" class="secondary">Regenerate</button>
      </div>
      <p class="muted">Rotates automatically once a day. Regenerating here does the same thing manually, on demand.</p>
    </div>

    <div class="card">
      <label>Max photo-scan attempts per round</label>
      <div class="row" style="align-items:center">
        <input type="number" id="scanLimitInput" min="1" max="20" step="1"
          value="${maxScanAttempts ?? ''}" style="flex:1" />
        <button type="button" id="saveScanLimitBtn" class="secondary">Save</button>
      </div>
      <p class="muted">
        Each recognition attempt is a real, paid API call. Once a round hits this limit, Scan Photo
        disables itself and the player is directed to enter the hand manually.
      </p>
    </div>

    <div class="card">
      <h2>Active Sessions (${sessions.length})</h2>
      ${sessions.length === 0 ? '<p class="muted">Nobody is currently active.</p>' : sessionRows}
      ${
        sessions.length > 0
          ? '<button type="button" id="kickAllBtn" class="danger" style="width:100%;margin-top:1rem">Kick Everyone</button>'
          : ''
      }
    </div>

    <button type="button" id="backBtn" class="ghost" style="width:100%">Back</button>
  `;

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());

  const runAction = (fn: () => Promise<void>) => {
    void (async () => {
      try {
        await fn();
        await loadAndRender(root, actions);
      } catch (e) {
        if (e instanceof AdminAuthError) {
          renderSecretPrompt(root, actions, 'Incorrect admin secret.');
          return;
        }
        renderScreen(
          root,
          actions,
          code,
          sessions,
          venues,
          maxScanAttempts,
          'That action failed. Check your connection and try again.',
        );
      }
    })();
  };

  root.querySelector<HTMLButtonElement>('#regenerateBtn')!.addEventListener('click', () => {
    runAction(async () => {
      await regenerateActivationCode();
    });
  });

  root.querySelector<HTMLButtonElement>('#saveScanLimitBtn')!.addEventListener('click', () => {
    const input = root.querySelector<HTMLInputElement>('#scanLimitInput')!;
    const value = Number(input.value);
    runAction(async () => {
      await saveMaxScanAttemptsApi(value);
    });
  });

  root.querySelectorAll<HTMLButtonElement>('.kick-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      runAction(async () => {
        await kickSession(btn.dataset.sessionId!);
      });
    });
  });

  root.querySelectorAll<HTMLButtonElement>('.kick-block-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      runAction(async () => {
        await kickAndBlockSession(btn.dataset.sessionId!);
      });
    });
  });

  root.querySelector<HTMLButtonElement>('#kickAllBtn')?.addEventListener('click', () => {
    runAction(async () => {
      await revokeAllSessionsApi();
    });
  });
}
